/**
 * Agency portal global scope (SYR-85).
 *
 * One portal-wide data window (period preset) consumed by Portfolio, Compare
 * and Client Scorecard. Mirrors the marketer-view scope pattern; persisted so
 * the agency's chosen window survives reloads.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';

export type AgencyPeriod =
    | '30_days'
    | '60_days'
    | '90_days'
    | '120_days'
    | '180_days'
    | '365_days'
    | 'all_time';

export const AGENCY_PERIODS: AgencyPeriod[] = [
    '30_days',
    '60_days',
    '90_days',
    '120_days',
    '180_days',
    '365_days',
    'all_time',
];

export const AGENCY_PERIOD_LABELS: Record<AgencyPeriod, { id: string; def: string }> = {
    '30_days': { id: 'agency.period.30', def: 'Last 30 days' },
    '60_days': { id: 'agency.period.60', def: 'Last 60 days' },
    '90_days': { id: 'agency.period.90', def: 'Last 90 days' },
    '120_days': { id: 'agency.period.120', def: 'Last 120 days' },
    '180_days': { id: 'agency.period.180', def: 'Last 180 days' },
    '365_days': { id: 'agency.period.365', def: 'Last 365 days' },
    all_time: { id: 'agency.period.allTime', def: 'All time' },
};

const STORAGE_KEY = 'agencyPortalPeriod';
const DEFAULT_PERIOD: AgencyPeriod = '180_days';

interface AgencyScopeValue {
    period: AgencyPeriod;
    setPeriod: (period: AgencyPeriod) => void;
}

const AgencyScopeContext = createContext<AgencyScopeValue>({
    period: DEFAULT_PERIOD,
    setPeriod: () => undefined,
});

export const AgencyScopeProvider: React.FunctionComponent<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [period, setPeriod] = useState<AgencyPeriod>(() => {
        const stored = localStorage.getItem(STORAGE_KEY) as AgencyPeriod | null;
        return stored && AGENCY_PERIODS.includes(stored) ? stored : DEFAULT_PERIOD;
    });

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, period);
    }, [period]);

    return (
        <AgencyScopeContext.Provider value={{ period, setPeriod }}>
            {children}
        </AgencyScopeContext.Provider>
    );
};

export const useAgencyScope = (): AgencyScopeValue => useContext(AgencyScopeContext);
